import React, { useRef, useState } from 'react';
import { Search, X, QrCode } from 'lucide-react';

export default function SearchBar({
  value,
  onChange,
  placeholder = 'Tìm kiếm sản phẩm, thương hiệu, danh mục...'
}) {
  const inputRef = useRef(null);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    inputRef.current?.focus();
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  const handleButtonClick = (e) => {
    // When clicking the 'TÌM KIẾM' button, guarantee focus to open virtual keyboard on mobile
    inputRef.current?.focus();
  };

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-2 sm:gap-3 w-full max-w-2xl justify-center"
      >
        {/* Left Side: Coffee Support QR Code */}
        <button
          type="button"
          onClick={() => setIsQrModalOpen(true)}
          className="w-9 h-9 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-xl sm:rounded-2xl overflow-hidden border-2 border-[#F5A623] ring-1 ring-[#D81B27]/50 shadow-lg shrink-0 bg-white p-0.5 hover:scale-105 active:scale-95 transition duration-200 cursor-pointer"
          title="Ủng hộ mình 1 ly caffe nhé ☕"
        >
          <img
            src="/images/qr-acb.png"
            alt="Ủng hộ mình 1 ly caffe nhé"
            className="w-full h-full object-contain rounded-lg sm:rounded-xl pointer-events-none"
            loading="eager"
          />
        </button>

        {/* Main Search Input Capsule (Mockup Style) */}
        <div
          onClick={handleContainerClick}
        className="flex-1 flex items-center bg-[#250408]/95 border border-[#6E141E] focus-within:border-[#F5A623] focus-within:ring-2 focus-within:ring-[#F5A623]/30 rounded-full pl-2.5 sm:pl-3.5 pr-1.5 py-1 sm:py-1.5 shadow-2xl transition duration-150 min-w-0 cursor-text"
      >
        <Search
          size={18}
          className="text-[#F5A623] shrink-0 ml-0.5 sm:ml-1 mr-1.5 sm:mr-2 pointer-events-none drop-shadow-xs"
        />
        <input
          ref={inputRef}
          type="text"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-transparent text-sm sm:text-sm text-[#FFF5E6] placeholder-amber-200/40 focus:outline-none min-w-0 py-1"
        />
        {value && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
              inputRef.current?.focus();
            }}
            className="p-1 text-amber-200/60 hover:text-white mr-1 transition shrink-0"
            title="Xóa tìm kiếm"
          >
            <X size={14} />
          </button>
        )}
        <button
          type="submit"
          onClick={handleButtonClick}
          className="bg-gradient-to-r from-[#D81B27] via-[#E53935] to-[#F57C00] text-white text-[10px] sm:text-xs font-black uppercase tracking-wider px-3.5 sm:px-6 py-1.5 sm:py-2 rounded-full border border-[#FF8A80]/60 shadow-md shadow-red-950/70 hover:brightness-110 active:scale-95 transition shrink-0 whitespace-nowrap cursor-pointer"
        >
          TÌM KIẾM
        </button>
      </div>

      {/* Right Featured Thumbnail: iPhone (Image 3) */}
      <div
        className="w-9 h-9 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-xl sm:rounded-2xl overflow-hidden border-2 border-[#F5A623] ring-1 ring-[#D81B27]/50 shadow-lg shrink-0 bg-[#2D060A]"
        title="Công nghệ đỉnh cao"
      >
        <img
          src="/images/search-iphone.png"
          alt="Điện thoại công nghệ"
          className="w-full h-full object-cover object-center hover:scale-110 transition duration-300 pointer-events-none"
          loading="eager"
        />
      </div>
    </form>

    {/* ACB Bank Transfer QR Popup Modal */}
    {isQrModalOpen && (
      <div
        onClick={() => setIsQrModalOpen(false)}
        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-4 animate-fade-in"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-[#230407] border-2 border-[#F5A623] rounded-3xl p-5 max-w-xs w-full text-center shadow-2xl relative animate-scale-up"
        >
          <button
            type="button"
            onClick={() => setIsQrModalOpen(false)}
            className="absolute top-3 right-3 p-1.5 rounded-full text-amber-200/70 hover:text-white hover:bg-white/10 transition"
            title="Đóng"
          >
            <X size={18} />
          </button>
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#FFE29A] to-[#F5A623] text-[#240306] flex items-center justify-center mx-auto mb-2 shadow-md">
            <span className="text-xl">☕</span>
          </div>
          <h3 className="text-base font-bold text-[#FFE29A] mb-1">
            Ủng hộ mình 1 ly caffe nhé
          </h3>
          <p className="text-xs text-amber-200/70 mb-3">
            Cảm ơn bạn đã ghé thăm và đồng hành cùng Lại AI!
          </p>
          <div className="bg-white p-2.5 rounded-2xl border-2 border-[#F5A623]/40 shadow-inner inline-block mx-auto">
            <img
              src="/images/qr-acb.png"
              alt="Mã QR chuyển khoản ACB"
              className="w-56 h-56 object-contain rounded-xl"
            />
          </div>
          <div className="mt-3 py-1.5 px-3 rounded-full bg-[#34070B] border border-[#5E0F17] inline-flex items-center gap-2 text-xs font-bold text-[#FFF5EB]">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
            <span>Ngân hàng TMCP Á Châu (ACB)</span>
          </div>
        </div>
      </div>
    )}
  </>
  );
}
